<template>
  <div class="rn">
    <!-- Composer: nueva nota -->
    <div class="rn-composer">
      <textarea
        v-model="draft"
        class="rn-textarea"
        rows="3"
        :placeholder="$t('reservasPanels.notes.composerPlaceholder')"
        @keydown.meta.enter="addNote"
        @keydown.ctrl.enter="addNote"
      ></textarea>
      <div class="rn-composer__foot">
        <span class="rn-hint">{{ $t('reservasPanels.notes.saveHint') }}</span>
        <button
          type="button"
          class="rn-btn rn-btn--primary"
          :disabled="!draft.trim() || saving"
          @click="addNote"
        >
          <span v-if="saving" class="rn-spinner"></span>
          <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          <span>{{ saving ? $t('reservasPanels.notes.saving') : $t('reservasPanels.notes.addNote') }}</span>
        </button>
      </div>
    </div>

    <!-- Lista de notas -->
    <div v-if="loading" class="rn-loading">
      <span class="rn-spinner rn-spinner--dark"></span> {{ $t('reservasPanels.notes.loading') }}
    </div>

    <div v-else-if="!notes.length" class="rn-empty">
      <div class="rn-empty__badge">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="9" y1="13" x2="15" y2="13" /><line x1="9" y1="17" x2="13" y2="17" />
        </svg>
      </div>
      <h4>{{ $t('reservasPanels.notes.emptyTitle') }}</h4>
      <p>{{ $t('reservasPanels.notes.emptyText') }}</p>
    </div>

    <transition-group v-else name="rn-item" tag="div" class="rn-list">
      <div v-for="note in notes" :key="note.id" class="rn-note">
        <!-- Edición inline -->
        <template v-if="editingId === note.id">
          <textarea v-model="editDraft" class="rn-textarea" rows="3"></textarea>
          <div class="rn-note__actions">
            <button type="button" class="rn-btn rn-btn--ghost" @click="cancelEdit">{{ $t('reservasPanels.notes.cancel') }}</button>
            <button type="button" class="rn-btn rn-btn--primary" :disabled="!editDraft.trim()" @click="saveEdit(note)">{{ $t('reservasPanels.notes.save') }}</button>
          </div>
        </template>

        <!-- Vista normal -->
        <template v-else>
          <p class="rn-note__content">{{ note.content }}</p>
          <div class="rn-note__foot">
            <span class="rn-note__meta">
              <span class="rn-note__avatar">{{ initials(note.user) }}</span>
              <span class="rn-note__author">{{ note.user ? note.user.name : $t('reservasPanels.notes.team') }}</span>
              <span class="rn-note__dot"></span>
              <span class="rn-note__date">{{ relativeDate(note.created_at) }}</span>
            </span>

            <div v-if="confirmingId === note.id" class="rn-note__confirm">
              <span>{{ $t('reservasPanels.notes.confirmDelete') }}</span>
              <button type="button" class="rn-btn rn-btn--danger" @click="removeNote(note)">{{ $t('reservasPanels.notes.yes') }}</button>
              <button type="button" class="rn-btn rn-btn--ghost" @click="confirmingId = null">{{ $t('reservasPanels.notes.no') }}</button>
            </div>
            <div v-else class="rn-note__tools">
              <button type="button" class="rn-icon-btn" :title="$t('reservasPanels.notes.editTitle')" @click="startEdit(note)">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                </svg>
              </button>
              <button type="button" class="rn-icon-btn rn-icon-btn--danger" :title="$t('reservasPanels.notes.deleteTitle')" @click="confirmingId = note.id">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
              </button>
            </div>
          </div>
        </template>
      </div>
    </transition-group>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import { $toast } from "@/utils/useToast";

export default {
  name: "ReservationNotes",

  props: {
    reservationData: { type: Object, required: false, default: null },
  },

  data() {
    return {
      notes: [],
      draft: "",
      editingId: null,
      editDraft: "",
      confirmingId: null,
      loading: true,
      saving: false,
    };
  },

  computed: {
    reservationId() {
      return this.reservationData?.id;
    },
  },

  watch: {
    reservationId: {
      immediate: true,
      handler(id) {
        if (id) this.load();
      },
    },
  },

  methods: {
    async load() {
      if (!this.reservationId) return;
      this.loading = true;
      try {
        const { data } = await axios.get(`/reservations/${this.reservationId}/notes`);
        this.notes = Array.isArray(data?.data) ? data.data : [];
      } catch (e) {
        $toast.error(this.$t("reservasPanels.notes.loadError"));
      } finally {
        this.loading = false;
      }
    },

    async addNote() {
      const content = this.draft.trim();
      if (!content || this.saving) return;
      this.saving = true;
      try {
        const { data } = await axios.post(`/reservations/${this.reservationId}/notes`, { content });
        if (data?.data) this.notes.unshift(data.data);
        this.draft = "";
        $toast.success(this.$t("reservasPanels.notes.added"));
        this.$emit("notes-changed");
      } catch (e) {
        $toast.error(this.$t("reservasPanels.notes.saveError"));
      } finally {
        this.saving = false;
      }
    },

    startEdit(note) {
      this.confirmingId = null;
      this.editingId = note.id;
      this.editDraft = note.content;
    },

    cancelEdit() {
      this.editingId = null;
      this.editDraft = "";
    },

    async saveEdit(note) {
      const content = this.editDraft.trim();
      if (!content) return;
      try {
        const { data } = await axios.put(
          `/reservations/${this.reservationId}/notes/${note.id}`,
          { content }
        );
        const idx = this.notes.findIndex((n) => n.id === note.id);
        if (idx !== -1 && data?.data) this.$set(this.notes, idx, data.data);
        this.cancelEdit();
        $toast.success(this.$t("reservasPanels.notes.updated"));
        this.$emit("notes-changed");
      } catch (e) {
        $toast.error(this.$t("reservasPanels.notes.updateError"));
      }
    },

    async removeNote(note) {
      try {
        await axios.delete(`/reservations/${this.reservationId}/notes/${note.id}`);
        this.notes = this.notes.filter((n) => n.id !== note.id);
        this.confirmingId = null;
        $toast.success(this.$t("reservasPanels.notes.deleted"));
        this.$emit("notes-changed");
      } catch (e) {
        $toast.error(this.$t("reservasPanels.notes.deleteError"));
      }
    },

    initials(user) {
      const name = String(user?.name || this.$t("reservasPanels.notes.team")).trim();
      const parts = name.split(/\s+/).filter(Boolean);
      return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "··";
    },

    relativeDate(d) {
      if (!d) return "";
      return moment(d).fromNow();
    },
  },
};
</script>

<style lang="scss" scoped>
.rn {
  font-family: "DM Sans", sans-serif;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 18px 18px 16px;
}

/* ── Composer ── */
.rn-composer {
  border: 1px solid #ecedf2;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  transition: border-color 0.16s ease, box-shadow 0.16s ease;

  &:focus-within {
    border-color: #cdd2e6;
    box-shadow: 0 4px 14px -6px rgba(16, 24, 40, 0.12);
  }
}

.rn-textarea {
  width: 100%;
  border: none;
  background: transparent;
  resize: vertical;
  padding: 12px 14px;
  font-family: "DM Sans", sans-serif;
  font-size: 13.5px;
  line-height: 1.55;
  color: #0e1320;
  outline: none;
  min-height: 64px;

  &::placeholder {
    color: #9aa2b5;
  }
}

.rn-composer__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 12px;
  border-top: 1px solid #f3f4f8;
}

.rn-hint {
  font-size: 11px;
  color: #9aa2b5;
  font-weight: 500;
}

/* ── Botones ── */
.rn-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 10px;
  border: 1px solid transparent;
  font-family: "DM Sans", sans-serif;
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.16s ease, box-shadow 0.16s ease, background 0.16s ease,
    color 0.16s ease, border-color 0.16s ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.rn-btn--primary {
  background: #11b14d;
  color: #fff;
  box-shadow: 0 1px 2px rgba(17, 177, 77, 0.35);

  &:not(:disabled):hover {
    background: #0a8a3a;
    transform: translateY(-1px);
    box-shadow: 0 6px 16px -4px rgba(17, 177, 77, 0.4);
  }
}

.rn-btn--ghost {
  background: #fff;
  color: #3a4156;
  border-color: #ecedf2;

  &:hover {
    background: #f7f8fb;
  }
}

.rn-btn--danger {
  background: #fee2e2;
  color: #dc2626;

  &:hover {
    background: #fecaca;
  }
}

/* ── Lista ── */
.rn-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.rn-note {
  border: 1px solid #ecedf2;
  border-radius: 14px;
  background: #fff;
  padding: 13px 15px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  transition: border-color 0.16s ease, box-shadow 0.16s ease;

  &:hover {
    border-color: #e3e5ee;
    box-shadow: 0 6px 16px -8px rgba(16, 24, 40, 0.12);
  }
}

.rn-note__content {
  margin: 0 0 10px;
  font-size: 13.5px;
  line-height: 1.55;
  color: #0e1320;
  white-space: pre-wrap;
  word-break: break-word;
}

.rn-note__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.rn-note__meta {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
  font-size: 11.5px;
  color: #6b7388;
}

.rn-note__avatar {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: inline-grid;
  place-items: center;
  background: linear-gradient(135deg, #eef0fc, #e1d6fb);
  color: #5028c7;
  font-size: 9.5px;
  font-weight: 800;
  flex-shrink: 0;
}

.rn-note__author {
  font-weight: 700;
  color: #3a4156;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rn-note__dot {
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: #c4cad6;
  flex-shrink: 0;
}

.rn-note__date {
  white-space: nowrap;
}

.rn-note__tools,
.rn-note__confirm {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.rn-note__confirm {
  font-size: 11.5px;
  color: #6b7388;

  .rn-btn {
    padding: 4px 9px;
    font-size: 11.5px;
  }
}

.rn-icon-btn {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid #ecedf2;
  background: #fff;
  color: #6b7388;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: all 0.16s ease;

  &:hover {
    background: #f7f8fb;
    color: #0e1320;
  }

  &--danger:hover {
    background: #fef2f2;
    color: #dc2626;
    border-color: #fecaca;
  }
}

.rn-note__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 10px;
}

/* ── Estados ── */
.rn-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 18px;
  justify-content: center;
  color: #6b7388;
  font-size: 13px;
}

.rn-empty {
  text-align: center;
  padding: 28px 16px;

  &__badge {
    width: 56px;
    height: 56px;
    border-radius: 16px;
    display: grid;
    place-items: center;
    margin: 0 auto 12px;
    background: linear-gradient(135deg, #f0eafd, #eef0fc);
    border: 1px solid #ecedf2;
    color: #673de6;
  }

  h4 {
    margin: 0 0 4px;
    font-size: 14px;
    font-weight: 700;
    color: #0e1320;
  }

  p {
    margin: 0 auto;
    max-width: 230px;
    font-size: 12.5px;
    color: #6b7388;
    line-height: 1.5;
  }
}

/* ── Spinners ── */
.rn-spinner {
  width: 13px;
  height: 13px;
  border: 2px solid rgba(255, 255, 255, 0.5);
  border-top-color: #fff;
  border-radius: 50%;
  animation: rn-spin 0.7s linear infinite;

  &--dark {
    border-color: #d3d7e1;
    border-top-color: #6b7388;
  }
}

@keyframes rn-spin {
  to {
    transform: rotate(360deg);
  }
}

/* ── Transición de la lista (añadir/eliminar/mover) ── */
.rn-item-enter-active,
.rn-item-leave-active {
  transition: opacity 0.3s ease, transform 0.3s cubic-bezier(0.22, 0.8, 0.32, 1);
}
.rn-item-enter {
  opacity: 0;
  transform: translateY(-8px);
}
.rn-item-leave-to {
  opacity: 0;
  transform: translateX(20px);
}
.rn-item-leave-active {
  position: absolute;
  width: 100%;
}
.rn-item-move {
  transition: transform 0.3s cubic-bezier(0.22, 0.8, 0.32, 1);
}
</style>
